import React, { createContext, useContext, useMemo } from "react";
import type { HostAPI } from "./HostAPI";
import { ShopifyHostAPI } from "./shopify/ShopifyHostAPI";
import { StandaloneHostAPI } from "./standalone/StandaloneHostAPI";
import { useOptionalAppBridge } from "./shopify/AppBridgeProvider";

export type HostKind = "shopify" | "standalone";

const HostContext = createContext<HostAPI | null>(null);

export const useHost = (): HostAPI => {
  const ctx = useContext(HostContext);
  if (!ctx) {
    throw new Error("HostContext missing");
  }
  return ctx;
};

export function detectHost(): HostKind {
  if (typeof window === "undefined") {
    return "standalone";
  }
  const qp = new URLSearchParams(window.location.search);
  if (
    window.location.pathname.startsWith("/embedded") ||
    (qp.get("shop") && qp.get("host"))
  ) {
    return "shopify";
  }
  return "standalone";
}

export const HostProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const host = useMemo(() => detectHost(), []);
  const appBridge = useOptionalAppBridge();

  const api: HostAPI = useMemo(() => {
    if (host === "shopify") {
      if (!appBridge) {
        console.warn("[host] Missing App Bridge instance; falling back to standalone host");
        return new StandaloneHostAPI();
      }
      return new ShopifyHostAPI(appBridge);
    }
    return new StandaloneHostAPI();
  }, [appBridge, host]);

  return <HostContext.Provider value={api}>{children}</HostContext.Provider>;
};
